/**
 * The whole-cohort threshold state.
 *
 * When fewer than ten students have claimed the institution, the page does not
 * render six sections of withheld cards. It renders this, and then the claim
 * centre, and then the sections that do not depend on the cohort at all.
 *
 * ── WHY THIS IS A DESIGNED SCREEN AND NOT A DASHBOARD WITH HOLES ─────────
 * A new tenant sees this on the day they sign, and what they need on that day
 * is not analytics, it is a way to get their students in. So below the
 * threshold the product is a claiming tool and above it becomes an analytics
 * tool, and that transition is the onboarding. Designing it that way turns the
 * hardest constraint in the product into its cleanest first-run experience.
 *
 * ── THERE ARE NO SAMPLE NUMBERS IN THE PREVIEW ───────────────────────────
 * Not greyed numbers, not illustrative numbers, none. Showing a plausible fake
 * figure to a paying customer on day one is the exact failure this whole
 * rebuild is correcting. The preview below names the four sections that turn
 * on and says what each will hold, in words.
 *
 * ── THE ONE PLACE A RAW NUMBER IS RENDERED ───────────────────────────────
 * The progress meter states how far the roster is from ten. That is the
 * foundation's own `StateCard` progress prop, shipped for this state, and the
 * admin IA spec prints the figure in both of its worked examples: a threshold
 * state that will not say how far you are from the threshold is not a
 * threshold state. It is scoped to the institution's own claim count, which is
 * the one figure they already know from their own outreach, and it is never
 * rendered as a metric anywhere else on the surface.
 */

import {
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  StateCard,
  buttonClass,
} from "~/app/components/primitives";
import { formatCount, pluralize } from "~/app/lib/format";
import type { CohortView } from "~/app/lib/demo-data";
import { K } from "~/lib/suppression";

/* In page order (Sep 10 2026: the Activity group, then Cohort). */
const WHAT_TURNS_ON: readonly { title: string; body: string }[] = [
  {
    title: "Overview",
    body: "Your claimed count against your seats, the mentorships your students hold, and a weekly curve of the roster as it grows.",
  },
  {
    title: "Mentorship",
    body: "How many of your students hold a mentorship, with the denominator and the interval, and how that compares with students across P3.",
  },
  {
    title: "Engagement",
    body: "The opportunities your students apply to, the questions they ask, and how often they use the app, with or without a mentor.",
  },
  {
    title: "Milestones",
    body: "The milestones your students mark complete and the outcomes they report along the way, labeled as student reported everywhere they appear.",
  },
  {
    title: "Mentee Goals",
    body: "The support areas, industries, education goals and meeting rhythms your own students chose when they set up their profiles.",
  },
];

/**
 * Three states, one card (Sep 10 2026, the live dashboard). The claimed
 * count arrives as a Cell, and its kind is the state:
 *
 *   value        a real zero, or a count under the floor on the sample. The
 *                meter reads how far the roster is from ten.
 *   suppressed   one to nine students on a live tenant. The Worker withholds
 *                the exact count, because a count under ten IS a figure about
 *                fewer than ten people, so there is no meter and no number.
 *   unavailable  no snapshot yet. Nothing has been computed, and the copy
 *                says that rather than showing a zero that is not one.
 */
export function CohortBuilding({
  view,
  invitationsHref,
  cohort,
}: {
  view: CohortView;
  /** The Invitations screen (Sep 17 2026): on day one the product is the
   *  join link, and since it left the scrolling page the card that says so
   *  has to lead there. */
  invitationsHref?: string;
  /** The same state for one of the institution's own cohorts (Sep 17 2026):
   *  the floor applies to it alone, and the way past it is Enrollment, where
   *  students are placed in a cohort, not the join link. */
  cohort?: { readonly name: string; readonly enrollmentHref: string };
}) {
  const claimed = view.claimed;
  const known = claimed.kind === "value" ? claimed.value : null;
  const remaining = known === null ? null : Math.max(0, K - known);
  const title = cohort
    ? claimed.kind === "unavailable"
      ? `Figures for ${cohort.name} are being prepared`
      : known === 0
        ? `No student is in ${cohort.name} yet`
        : `${cohort.name} is building`
    : claimed.kind === "unavailable"
      ? "Your first figures are being prepared"
      : known === 0
        ? "No students have claimed you yet"
        : "Your cohort is building";

  return (
    <section className="e-sec e-print-page" style={{ marginTop: 0 }}>
      <Card span="full">
        <CardHeader>
          <CardTitle
            id="cohort-building"
            sub={
              cohort
                ? `${cohort.name}, ${view.institution.legalName}`
                : `${view.institution.legalName}, ${view.institution.term.name}`
            }
          >
            {cohort ? `Figures for this cohort appear at ${K} students` : `Your dashboard turns on at ${K} claimed students`}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <StateCard
            variant="building"
            title={title}
            progress={
              known === null ? undefined : { have: known, need: K, noun: cohort ? "students in the cohort" : "students claimed" }
            }
            action={
              cohort ? (
                <a className={buttonClass("primary", "sm")} href={cohort.enrollmentHref}>
                  Place students on Enrollment
                </a>
              ) : invitationsHref ? (
                <a className={buttonClass("primary", "sm")} href={invitationsHref}>
                  Open Invitations
                </a>
              ) : undefined
            }
          >
            {claimed.kind === "unavailable" ? (
              cohort ? (
                <>P3 has not computed this cohort's figures yet. They are prepared on the next hourly recomputation. </>
              ) : (
                <>
                  P3 has not computed your figures yet. Your join link and code
                  already work, and this page fills in from the first computation.{" "}
                </>
              )
            ) : claimed.kind === "suppressed" ? (
              cohort ? (
                <>
                  Between one and nine students are in this cohort. The exact count is not shown, because it would
                  be a figure about fewer than {K} people.{" "}
                </>
              ) : (
                <>
                  Between one and nine students have claimed you. The exact count
                  is not shown, because it would be a figure about fewer than {K}{" "}
                  people.{" "}
                </>
              )
            ) : null}
            P3 does not publish any figure that would represent fewer than {K}{" "}
            students. That is the same threshold state education agencies use in
            public reporting, and it is a commitment in your Data Sharing
            Addendum. Every section below turns on the moment you cross it.
            {remaining !== null && remaining > 0 ? (
              <>
                {" "}
                {formatCount(remaining)} {pluralize(remaining, "student")} to go.
              </>
            ) : null}
          </StateCard>
        </CardBody>
        <CardFootnote>
          <p className="e-note">
            {cohort
              ? "Nothing changes for the students in it. What waits is the reporting for this cohort, not the service."
              : "Nothing is blocked for your students in the meantime. They can join, match with a mentor and use the app exactly as they will later. What waits is the reporting, not the service."}
          </p>
        </CardFootnote>
      </Card>

      <div className="e-grid">
        <Card span="full">
          <CardHeader>
            <CardTitle sub="Named now, so nothing is a surprise later.">
              What turns on when you cross {K}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="e-absent">
              {WHAT_TURNS_ON.map((item) => (
                <li key={item.title}>
                  <div className="e-absent-head">
                    <p className="e-h3">{item.title}</p>
                  </div>
                  <p className="e-absent-why">{item.body}</p>
                </li>
              ))}
            </ul>
          </CardBody>
          <CardFootnote>
            <p className="e-note">
              Groups inside a section have their own threshold. A cohort of
              twelve students crosses the page-level floor while most breakdowns
              inside it stay withheld, and that is the rule working rather than a
              gap in the data.
            </p>
          </CardFootnote>
        </Card>
      </div>

    </section>
  );
}
